/**
 * MUI theme factory for Brand Center's own chrome, in the **dink-admin design
 * language** (SASBND-0008 U1): court green, Roboto + Roboto Mono, the handoff's
 * shadows/radii/density.
 *
 * The literal values live in `./tokens`, which is a marked, vendored mirror of
 * dink-admin `src/theme/tokens.ts` — tune the design there (and in dink-admin),
 * never here. Everything mode-dependent arrives as a `ChromeTokens` argument, so
 * the same factory builds both modes: `LIGHT_CHROME` and, since U2,
 * `DARK_CHROME`. `ThemeModeProvider` chooses between them.
 *
 * This app is a brand *editor*, so its frame deliberately does NOT theme itself
 * from the tenant's brand — the tenant's `BrandDto` is the data being edited and
 * is rendered only inside the editor's preview (api-contract-handoff §5).
 */
import { createTheme, type Theme } from '@mui/material/styles';
import type { CSSProperties } from 'react';
import {
  COURT_GREEN,
  COURT_GREEN_DARK,
  LIGHT_CHROME,
  MONO,
  SANS,
  SEMANTIC,
  type ChromeTokens,
} from './tokens';

// ---- Typography augmentation: a reusable, typed `mono` treatment ----
// HTML source readouts, merge tokens and IDs render in Roboto Mono. Adding it to
// the theme's typography variants makes it usable as <Typography variant="mono">
// and via theme.typography.mono.
declare module '@mui/material/styles' {
  interface TypographyVariants {
    mono: CSSProperties;
  }
  interface TypographyVariantsOptions {
    mono?: CSSProperties;
  }
  // The recessed surface behind code readouts and preview stages. Exposing it on
  // the palette is what lets components say `bgcolor: 'background.subtle'`
  // instead of an ad-hoc `grey.50`, so they follow the mode automatically.
  interface TypeBackground {
    subtle: string;
  }
}
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    mono: true;
  }
}

export function createAppTheme(chrome: ChromeTokens = LIGHT_CHROME): Theme {
  return createTheme({
    palette: {
      mode: chrome.mode,
      primary: { main: COURT_GREEN, dark: COURT_GREEN_DARK, contrastText: '#FFFFFF' },
      success: SEMANTIC.success,
      warning: SEMANTIC.warning,
      error: SEMANTIC.error,
      // dink-admin defines no `info` because it never renders `color="info"`.
      // Brand Center does (the two "Unsaved changes" chips), so omitting it would
      // fall through to MUI's stock `#0288d1` blue — the exact generic blue this
      // unit exists to remove. Sourced from the vendored tonal-info pair rather
      // than invented; see the note in the delivery about its green reading.
      info: { main: chrome.alert.info.fg },
      background: {
        default: chrome.pageBg,
        paper: chrome.surface,
        subtle: chrome.surfaceSubtle,
      },
      text: chrome.text,
      divider: chrome.divider,
    },

    shape: { borderRadius: 8 }, // controls/inputs; cards override to 12 below.

    typography: {
      fontFamily: SANS,
      h1: { fontSize: 26, fontWeight: 500, letterSpacing: '-0.3px' },
      h2: { fontSize: 24, fontWeight: 500, letterSpacing: '-0.3px' },
      h3: { fontSize: 18, fontWeight: 500 },
      subtitle1: { fontSize: 15, fontWeight: 500 },
      body1: { fontSize: 13 },
      body2: { fontSize: 12.5, color: chrome.text.secondary },
      caption: { fontSize: 11.5 },
      button: {
        textTransform: 'uppercase',
        fontWeight: 500,
        letterSpacing: '0.4px',
        fontSize: 13,
      },
      // Reusable mono treatment for HTML source, merge tokens and IDs.
      mono: { fontFamily: MONO, fontSize: 13, letterSpacing: 0 },
    },

    components: {
      // Page background.
      MuiCssBaseline: {
        styleOverrides: { body: { backgroundColor: chrome.pageBg } },
      },

      // Cards / Paper: 12px radius + the handoff's layered card shadow.
      MuiPaper: {
        styleOverrides: {
          rounded: { borderRadius: 12 },
          elevation1: { boxShadow: chrome.shadows.card },
        },
      },

      MuiAppBar: {
        defaultProps: { color: 'inherit', elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: chrome.appBarBg,
            borderBottom: `1px solid ${chrome.divider}`,
            boxShadow: chrome.shadows.appBar,
          },
        },
      },

      MuiDrawer: {
        // Width stays with the layout (it also offsets the main column); the
        // theme owns only the rule between drawer and content.
        styleOverrides: {
          paper: { borderRight: `1px solid ${chrome.divider}` },
        },
      },

      // The drawer's nav items, mirroring dink-admin's `&.Mui-selected` block
      // (its `AppLayout.tsx`). `.active` is react-router's NavLink class, so the
      // tint lives here rather than as an sx in the layout — and only the theme
      // holds the mode-dependent `chrome` set, which the selected state needs to
      // stay legible in dark mode. The mode-invariant geometry (radius, height,
      // label weight) stays with the layout that owns the markup.
      MuiListItemButton: {
        styleOverrides: {
          root: {
            color: chrome.text.secondary,
            '& .MuiListItemIcon-root': { color: chrome.text.secondary },
            '&:hover': { backgroundColor: chrome.navHover },
            '&.active': {
              backgroundColor: chrome.navActive,
              color: chrome.navActiveFg,
              '& .MuiListItemIcon-root': { color: chrome.navActiveFg },
              '&:hover': { backgroundColor: chrome.navActiveHover },
            },
          },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 6,
            minHeight: 38,
            paddingInline: 16,
            // v9: the removed `containedPrimary` compound-class slot moves to a
            // `variants` entry (variant: 'contained' + color: 'primary').
            variants: [
              {
                props: { variant: 'contained', color: 'primary' },
                style: { '&:hover': { backgroundColor: COURT_GREEN_DARK } },
              },
            ],
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: { height: 24, borderRadius: 12, fontSize: 12, fontWeight: 500 },
          label: { paddingInline: 10 },
        },
      },

      // Dense tables by default.
      MuiTable: { defaultProps: { size: 'small' } },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: 13,
            paddingTop: 9,
            paddingBottom: 9,
            borderColor: chrome.dividerSubtle,
          },
          head: {
            fontSize: 12,
            fontWeight: 500,
            color: chrome.text.secondary,
            backgroundColor: chrome.surfaceSubtle,
            borderBottomColor: chrome.divider,
          },
        },
      },

      MuiTextField: { defaultProps: { variant: 'outlined', size: 'small' } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: COURT_GREEN,
            },
          },
        },
      },

      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 12.5,
            paddingInline: 14,
            borderColor: chrome.controlBorder,
            '&.Mui-selected': {
              backgroundColor: COURT_GREEN,
              color: '#fff',
              '&:hover': { backgroundColor: COURT_GREEN_DARK },
            },
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 8, boxShadow: chrome.shadows.dialog },
        },
      },

      // Tonal alerts for empty / permission / error states.
      MuiAlert: {
        styleOverrides: {
          // v9: the removed `standard{Error,Warning,Success,Info}` compound-class
          // slots move to `variants` entries (variant: 'standard' + each color).
          root: {
            borderRadius: 8,
            fontSize: 12.5,
            alignItems: 'flex-start',
            variants: [
              {
                props: { variant: 'standard', severity: 'error' },
                style: {
                  backgroundColor: chrome.alert.error.bg,
                  color: chrome.alert.error.fg,
                  border: chrome.alert.error.border,
                },
              },
              {
                props: { variant: 'standard', severity: 'warning' },
                style: {
                  backgroundColor: chrome.alert.warning.bg,
                  color: chrome.alert.warning.fg,
                  border: chrome.alert.warning.border,
                },
              },
              {
                props: { variant: 'standard', severity: 'success' },
                style: {
                  backgroundColor: chrome.alert.success.bg,
                  color: chrome.alert.success.fg,
                },
              },
              {
                props: { variant: 'standard', severity: 'info' },
                style: {
                  backgroundColor: chrome.alert.info.bg,
                  color: chrome.alert.info.fg,
                },
              },
            ],
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'uppercase',
            fontWeight: 500,
            fontSize: 13,
            letterSpacing: '0.4px',
            minHeight: 46,
          },
        },
      },

      MuiSkeleton: { defaultProps: { animation: 'pulse' } },
    },
  });
}
